// Where an order came from (spec/13 §5, F-POS-15; F-KIO-14): its channel, and — for a kiosk order — the
// station it was placed on and the player it is for. A pass bought at the kiosk "pay at the desk" is an
// unpaid kiosk order: it reads **Awaiting payment**, and taking payment here is what applies the pass to the
// player's session (the server does it on `commerce.sale.paid`).
//
// The station's name comes from the venue's stations (the same read the kitchen board uses); the player's
// from the profile read the Players screen shares — with `players:read`; without it, the short id stands in.

import type { Sale } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, type BadgeTone, type TableRowTone } from '@clavian/ui-web';
import { useMemo, type ReactNode } from 'react';

import { useStations } from '../../stations/queries';
import { SessionPlayer } from '../sessions/SessionPlayer';

/** A sale's state in words, with "Awaiting payment" for a kiosk order nobody has paid yet. */
export type OrderStatusKey = Sale['status'] | 'awaiting_payment';

/**
 * A kiosk order that is placed and unpaid — waiting for the desk (F-KIO-14) — or the order a console session placed
 * when it ended (ADR-0034), paid at the counter.
 */
export function isAwaitingPayment(sale: Pick<Sale, 'channel' | 'status'> & { readonly origin?: Sale['origin'] }): boolean {
  return (sale.channel === 'kiosk' || sale.origin === 'console_session') && sale.status === 'placed';
}

export function orderStatusKey(sale: Pick<Sale, 'channel' | 'status'> & { readonly origin?: Sale['origin'] }): OrderStatusKey {
  return isAwaitingPayment(sale) ? 'awaiting_payment' : sale.status;
}

export const ORDER_STATUS_TONE: Readonly<Record<OrderStatusKey, BadgeTone>> = {
  placed: 'info',
  awaiting_payment: 'warn',
  partially_paid: 'warn',
  paid: 'ok',
  partially_refunded: 'warn',
  refunded: 'neutral',
  cancelled: 'neutral',
};

/**
 * An order's state as the rail at its row's edge (ADR-0043): money still owed — placed, part paid, waiting for the desk
 * — wants a look (warn); a refunded or cancelled order is over (quiet); a paid one is plain.
 */
export function orderTone(sale: Sale): TableRowTone | undefined {
  if ((sale.status === 'placed' || sale.status === 'partially_paid') && sale.outstanding.amount > 0) return 'warn';
  if (sale.status === 'refunded' || sale.status === 'cancelled') return 'quiet';
  return undefined;
}

/** The station id → its label ("PC-01", or "PC 4" with no name), for the venue's stations. */
export function useStationLabels(organizationId: string, venueId: string): (stationId: string) => string | null {
  const { t } = useTranslation('console');
  const stations = useStations(organizationId, venueId);
  return useMemo(() => {
    const byId = new Map<string, string>();
    for (const station of (stations.data?.pages ?? []).flatMap((page) => page.data)) {
      byId.set(station.id, station.name ?? t('kitchen.pc', { number: station.number }));
    }
    return (stationId: string) => byId.get(stationId) ?? null;
  }, [stations.data, t]);
}

export interface OrderSourceProps {
  readonly organizationId: string;
  readonly sale: Pick<Sale, 'channel' | 'stationId' | 'playerId'> & { readonly origin?: Sale['origin'] };
  readonly stationLabel: (stationId: string) => string | null;
  readonly canReadPlayers: boolean;
}

/** The channel — a "Kiosk" badge for a kiosk order — then the station and the player it was placed for. */
export function OrderSource({ organizationId, sale, stationLabel, canReadPlayers }: OrderSourceProps): ReactNode {
  const { t } = useTranslation('console');
  const channel = t(`orders.channel.${sale.channel}`, { defaultValue: sale.channel });
  const station = sale.stationId === null ? null : (stationLabel(sale.stationId) ?? t('orders.source.station'));
  return (
    <span className="console-cell-sub">
      {sale.channel === 'kiosk' ? <Badge tone="info">{channel}</Badge> : channel}
      {sale.origin === 'console_session' ? (
        <>
          {' '}
          <Badge tone="info">{t('sell.awaiting.consoleSession')}</Badge>
        </>
      ) : null}
      {station === null ? null : <> · {station}</>}
      {sale.playerId === null ? null : (
        <>
          {' · '}
          <SessionPlayer organizationId={organizationId} playerId={sale.playerId} canReadPlayers={canReadPlayers} />
        </>
      )}
    </span>
  );
}
